import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { cn } from "@/lib/utils";
import { useStore } from "@/stores/useStore";
import { CARD_ZOOM_LEVELS } from "@/lib/const";
import {
  getActiveZoomIndex,
  getAdjacentZoomLevel,
  getZoomBarHeight,
} from "@/lib/cardZoom";
import { useCardSizeAnalytics } from "@/hooks/useCardSizeAnalytics";

export interface CardSizeControlProps {
  className?: string;
}

export const CardSizeControl = ({ className }: CardSizeControlProps) => {
  const zoom = useStore((s) => s.dashboardCardZoom);
  const setZoom = useStore((s) => s.setDashboardCardZoom);
  const { logCardSizeChanged } = useCardSizeAnalytics();

  const activeIndex = getActiveZoomIndex(zoom);
  const canZoomOut = activeIndex > 0;
  const canZoomIn = activeIndex < CARD_ZOOM_LEVELS.length - 1;

  const handleZoomChange = (newLevel: number, direction: "in" | "out") => {
    setZoom(newLevel);
    logCardSizeChanged(newLevel, direction);
  };

  return (
    <div
      className={cn(
        "flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-slate-200 px-2",
        className
      )}
    >
      <span className="text-xs font-semibold text-slate-500">Card size</span>

      <button
        type="button"
        aria-label="Decrease card size"
        disabled={!canZoomOut}
        onClick={() =>
          handleZoomChange(getAdjacentZoomLevel(zoom, "out"), "out")
        }
        className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Icon name={IconNames.minimize} size={16} />
      </button>

      <div
        className="flex items-end gap-0.5"
        role="group"
        aria-label="Card size levels"
      >
        {CARD_ZOOM_LEVELS.map((level, index) => (
          <button
            key={level}
            type="button"
            aria-label={`Set card size to level ${index + 1} of ${CARD_ZOOM_LEVELS.length}`}
            aria-pressed={index === activeIndex}
            onClick={() =>
              handleZoomChange(level, index > activeIndex ? "in" : "out")
            }
            className={cn(
              "w-1 rounded-sm bg-slate-400",
              index <= activeIndex && "bg-primary"
            )}
            style={{
              height: `${getZoomBarHeight(index, CARD_ZOOM_LEVELS.length)}px`,
            }}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="Increase card size"
        disabled={!canZoomIn}
        onClick={() => handleZoomChange(getAdjacentZoomLevel(zoom, "in"), "in")}
        className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        <Icon name={IconNames.plus} size={16} />
      </button>
    </div>
  );
};
